import { computed } from 'vue';
import { useFeatures } from '@/composables/useFeatures';
import { useViewerItemStore, ItemType } from '@/modules/viewer/store/useViewerItemStore';
import { useItemActions } from '@/modules/viewer/composables/useItemActions';
import { useUser } from '@/modules/viewer/composables/useUser';
import { storeToRefs } from 'pinia';

export enum BUTTON_TEST_ID {
  COPY = 'viewer-actions-copy',
  SHARE = 'viewer-actions-share',
  DOWNLOAD = 'viewer-actions-download',
  SAVE = 'viewer-actions-save',
  ANNOTATE = 'viewer-actions-annotate',
  EDIT = 'viewer-actions-edit',
}

export const useItemActionsToolbar = () => {
  const { getFeature } = useFeatures();
  const viewerItemStore = useViewerItemStore();
  const { isCurrentItemOwner } = useUser();
  const { isEditEditing, isEditProcessing } = storeToRefs(viewerItemStore);

  const {
    addButtonAction,
    addToCollectionAction,
    applyTemplateAction,
    customizePageAction,
    downloadAction,
    moveToTrashAction,
    openShareModalAction,
    openMergeVideoModalAction,
    openVersionsModalAction,
    toggleCommentsDisabledAction,
    toggleReactionsDisabledAction,
    toggleFavoriteAction,
  } = useItemActions();

  const ownerToolbarConfig = {
    separator: { isSeparator: true },
    download: {
      title: 'Download',
      name: 'download',
      icon: 'general.download',
      type: 'secondary',
      disabled: false,
      loading: false,
      action: downloadAction,
    },
    favorite: {
      remove: {
        title: 'Remove from Favorites',
        name: 'favorites-remove',
        icon: 'general.favoritesSolid',
        type: 'secondary',
        action: toggleFavoriteAction,
      },
      add: {
        title: 'Add to Favorites',
        name: 'favorites-add',
        icon: 'general.favorites',
        type: 'secondary',
        action: toggleFavoriteAction,
      },
    },
    collection: {
      title: 'Add to Collection',
      name: 'collection',
      icon: 'general.folder',
      type: 'secondary',
      action: addToCollectionAction,
    },
    versionsAndDuplicate: {
      title: 'Versions and Duplicate',
      name: 'versions',
      icon: 'general.versions',
      type: 'secondary',
      action: openVersionsModalAction,
    },
    mergeVideo: {
      title: 'Merge a video',
      name: 'merge-video',
      icon: 'general.mergeVideo',
      type: 'secondary',
      loading: false,
      disabled: false,
      action: openMergeVideoModalAction,
    },
    addButton: {
      title: 'Add Button',
      name: 'button',
      icon: 'general.button',
      type: 'secondary',
      action: addButtonAction,
    },
    applyTemplate: {
      title: 'Apply Template',
      name: 'template',
      icon: 'general.magicWand',
      type: 'secondary',
      action: applyTemplateAction,
    },
    accessSettings: {
      title: 'Access settings',
      name: 'settings',
      icon: 'general.lockKey',
      type: 'secondary',
      action: openShareModalAction,
    },
    customizePage: {
      title: 'Customize page',
      name: 'customize',
      icon: 'general.pencilAndRuler',
      type: 'secondary',
      action: customizePageAction,
    },
    comments: {
      title: 'Allow Comments',
      isToggle: true,
      name: 'comments',
      type: 'secondary',
      value: viewerItemStore.commentsDisabled,
      action: toggleCommentsDisabledAction,
    },
    reaction: {
      title: 'Allow Reactions',
      isToggle: true,
      name: 'reactions',
      type: 'secondary',
      value: viewerItemStore.reactionsDisabled,
      action: toggleReactionsDisabledAction,
    },
    trash: {
      title: 'Move to Trash',
      name: 'trash',
      icon: 'general.trash',
      type: 'danger',
      action: moveToTrashAction,
    },
  };

  const itemActionsMenu = computed(() => {
    if (!isCurrentItemOwner.value) {
      return null;
    }

    const toolbarMenu: any = [];
    // Download is visible only for images and videos
    switch (viewerItemStore.type) {
      case ItemType.IMAGE:
      case ItemType.VIDEO:
        ownerToolbarConfig.download.disabled = isEditProcessing.value;

        toolbarMenu.push(ownerToolbarConfig.download);
        break;
    }

    // Toggle favorite
    if (viewerItemStore.favorite) {
      toolbarMenu.push(ownerToolbarConfig.favorite.remove);
    } else {
      toolbarMenu.push(ownerToolbarConfig.favorite.add);
    }

    toolbarMenu.push(ownerToolbarConfig.collection, ownerToolbarConfig.separator);

    if (getFeature('version-history').value) {
      // Versions And Duplicate only for images and videos
      switch (viewerItemStore.type) {
        case ItemType.IMAGE:
        case ItemType.VIDEO:
          toolbarMenu.push(ownerToolbarConfig.versionsAndDuplicate, ownerToolbarConfig.separator);
          break;
      }
    }

    if (getFeature('merge-videos').value) {
      // Merge video only for videos
      if (viewerItemStore.type === ItemType.VIDEO) {
        ownerToolbarConfig.mergeVideo.disabled = isEditProcessing.value;
        ownerToolbarConfig.mergeVideo.loading = isEditEditing.value;

        toolbarMenu.push(ownerToolbarConfig.mergeVideo);
      }
    }

    toolbarMenu.push(
      ownerToolbarConfig.addButton,
      ownerToolbarConfig.applyTemplate,
      ownerToolbarConfig.accessSettings,
      ownerToolbarConfig.customizePage,
      ownerToolbarConfig.separator,
      { ...ownerToolbarConfig.comments, value: !viewerItemStore.commentsDisabled },
      { ...ownerToolbarConfig.reaction, value: !viewerItemStore.reactionsDisabled },
      ownerToolbarConfig.separator,
      ownerToolbarConfig.trash
    );

    return toolbarMenu;
  });

  return { itemActionsMenu };
};
